import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import { apiErrorText, apiRequest } from '../../utils';
import './MemberHome.css';
import './CompanyEvents.css';
import './CompanyTournament.css';

/* The standing of the day, for the organiser.

   ★★ The same query the back office has always shown (`fetch_teams_with_scores`), so there is one
   answer to "who is first" and not two. This screen adds no arithmetic of its own — that is the
   entire point of it.

   ★★ The battle points and the bonus points are shown APART. They come into being in completely
   different ways: the first are the sum of what happened in the heats, the second are awarded by
   hand (missions, cheers, challenge-others, awards) and sit on the team. A single total with no
   breakdown is a standing nobody can check, and the first question after a prize-giving is always
   "where did those points come from".

   ★★ **Which is why a row OPENS.** Underneath every team sit the heats it played, the place it took
   and what that place was worth, plus the six bonus columns by name — so the total can be added up
   by hand from the screen that shows it. Every line is a link to the score sheet of that heat: the
   answer to "that number is wrong" is the sheet that wrote it, not a second place to type it.

   ★★ **And the dial for what the field may see sits here too.** It writes `Event.leaderboardquarter`
   through the competition screen's own endpoint — one column, one writer — but it BELONGS on this
   screen: this is where somebody looks at the standing and decides what may go on the wall. The
   number beside the total says what the projection shows right now, and it comes from the board's
   own query, so the two cannot drift apart. */

const CompanyEventRanking = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { eventId } = useParams();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({});
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = useCallback(() => {
    apiRequest('GET', `/company/event/${eventId}/ranking`)
      .then(setData)
      .catch((e) => {
        setNotice(apiErrorText(t, e));
        navigate(`/company-event/${eventId}/tournament`);
      });
  }, [eventId, navigate, t]);

  useEffect(() => { load(); }, [load]);

  /* How much of the board the field may see. The same PUT the competition screen uses — the column
     has one writer, and a second endpoint onto it would be a second answer to "what is released". */
  const setReveal = (value) => {
    setBusy(true);
    setNotice(null);
    apiRequest('PUT', `/company/event/${eventId}/tournament/reveal`,
      { leaderboard_quarter: value })
      .then(() => load())
      .catch((e) => setNotice(apiErrorText(t, e)))
      .finally(() => setBusy(false));
  };

  if (!data) {
    return (
      <Base activeNav="home">
        <Card><p className="ct-field__hint">{notice || t('pleaseWait')}</p></Card>
      </Base>
    );
  }

  const teams = data.teams || [];
  const moments = data.moments || [];
  const anyBonus = teams.some((tm) => tm.bonus_points);
  const revealed = data.leaderboard_quarter;
  const capLifted = !!data.cap_lifted;

  /* The label of one playing moment on the dial: which game(s) it opens. "Game 4" alone is a number
     nobody can place in the day; "Game 4 — Tug of war" is the thing on the run sheet. */
  const momentLabel = (moment) => {
    const row = moments.find((m) => m.moment === moment);
    const games = ((row && row.games) || []).join(', ');
    return games
      ? t('rankingRevealMomentGames', { n: moment, games })
      : t('tournamentRevealMoment', { n: moment });
  };

  const toggle = (teamId) => setOpen((prev) => ({ ...prev, [teamId]: !prev[teamId] }));

  const openSheet = (game) => {
    if (!game.heat_id) return;
    navigate(`/company-event/${eventId}/scoring?heat=${game.heat_id}&back=ranking`);
  };

  /* The six hand-awarded columns, by name. Only the ones that carry something: a list of six zeros
     under every team is noise, and the point of the panel is the numbers that are THERE. */
  const bonusLines = (tm) => ([
    ['extra', 'rankingBonusExtra'],
    ['brain_labor', 'rankingBonusBrainLabor'],
    ['management', 'rankingBonusManagement'],
    ['supporter', 'rankingBonusSupporter'],
    ['bonus', 'rankingBonusOther'],
    ['challenge', 'rankingBonusChallenge'],
  ].map(([key, label]) => ({ key, label, value: Number((tm.bonus || {})[key] || 0) }))
    .filter((line) => line.value));

  return (
    <Base activeNav="home">
      <Card>
        <Back to={`/company-event/${eventId}/tournament`} />
        <Title my="1" className="mt-3">
          <Icon name="leaderboard" theme="primary" className="me-2" />
          {t('tournamentRanking')}
        </Title>

        {notice && <p className="ce-note ce-note--warn">{notice}</p>}

        {teams.length === 0 ? (
          <p className="ce-note">{t('rankingEmpty')}</p>
        ) : (
          <>
            {/* What the FIELD can see right now, next to what the organiser can see. Without this
                the two boards disagree and only one of them is on this screen. */}
            <p className="ct-field__hint">
              {capLifted
                ? t('rankingCapLifted')
                : (revealed == null || revealed === 0
                  ? t('rankingNotReleased')
                  : t('rankingReleasedUpTo', { n: revealed }))}
            </p>

            {/* -- how many games the board shows --
                ★★ On the screen where the standing is read, because that is where the decision is
                made. The column is NULL on almost every event on record, and NULL means "show
                nothing until the day is over" — which during a day being played reads as a broken
                projection rather than as a held-back one. */}
            {data.can_manage && moments.length > 0 && !capLifted && (
              <>
              <p className="ct-pick__label mt-2">{t('tournamentReveal')}</p>
              <span className="ct-field rk-reveal">
                <label htmlFor="rk-reveal">{t('tournamentRevealUpTo')}</label>
                <select id="rk-reveal" disabled={busy}
                        value={revealed == null ? '' : String(revealed)}
                        onChange={(e) => setReveal(e.target.value === '' ? null
                          : Number(e.target.value))}>
                  <option value="">{t('tournamentRevealNone')}</option>
                  {moments.map((m) => (
                    <option key={m.moment} value={String(m.moment)}>{momentLabel(m.moment)}</option>
                  ))}
                </select>
                <span className="ct-field__hint">{t('tournamentRevealHint')}</span>
              </span>
              </>
            )}

            {/* ⚠️ A heat whose run-sheet line has no playing moment is on no released standing at
                all while the day runs — the cap compares against a number that is not there. */}
            {data.heats_without_moment > 0 && !capLifted && (
              <p className="ce-note ce-note--warn">
                {t('rankingHeatsWithoutMoment', { count: data.heats_without_moment })}
              </p>
            )}

            <ol className="rk-list">
              {teams.map((tm, index) => {
                const isOpen = !!open[tm.team_id];
                const games = tm.games || [];
                /* The reproduction check. The panel adds up to the battle points the server sends,
                   because the three columns per heat are the three the standing sums — if they ever
                   disagree the standing is not reproducible and that is worth saying out loud. */
                const sum = games.reduce((acc, g) => acc + Number(g.points || 0), 0);
                const mismatch = Math.abs(sum - Number(tm.battle_points || 0)) > 0.01;
                const held = games.filter((g) => !g.revealed).length;
                return (
                  <li key={tm.team_id || index}
                      className={`rk-item${isOpen ? ' rk-item--open' : ''}`}>
                    <button type="button" className="rk-item__head"
                            onClick={() => toggle(tm.team_id)}>
                      <span className="rk-row__pos">{index + 1}</span>
                      <span className="rk-row__name">{tm.name}</span>
                      <span className="rk-row__split">
                        <span title={t('rankingBattlePoints')}>{Math.round(tm.battle_points)}</span>
                        {anyBonus && (
                          <span className="rk-row__bonus" title={t('rankingBonusPoints')}>
                            {`+${Math.round(tm.bonus_points)}`}
                          </span>
                        )}
                      </span>
                      <span className="rk-row__total">{Math.round(tm.points)}</span>
                      <Icon name={isOpen ? 'angle-small-up' : 'angle-small-down'}
                             className="rk-row__chev" />
                    </button>

                    {isOpen && (
                      <div className="rk-break">
                        {games.length === 0 ? (
                          <p className="ct-field__hint">{t('rankingNoGames')}</p>
                        ) : (
                          <ul className="rk-games">
                            {games.map((g) => (
                              <li key={g.row_id || g.heat_id}>
                                <button type="button"
                                        className={`rk-game${g.revealed ? '' : ' rk-game--held'}`}
                                        title={t('rankingOpenSheet')}
                                        onClick={() => openSheet(g)}>
                                  <span className="rk-game__when">
                                    {g.moment == null ? '—' : t('tournamentRevealMoment', { n: g.moment })}
                                  </span>
                                  <span className="rk-game__name">
                                    {g.game || g.acronym || '—'}
                                    {!g.revealed && !capLifted && (
                                      <em className="rk-game__held">{t('rankingHeldBack')}</em>
                                    )}
                                  </span>
                                  <span className="rk-game__rank">
                                    {g.is_disqualified
                                      ? t('scoringDisqualified')
                                      : (g.rank == null
                                        ? t('rankingNotScored')
                                        : t('rankingPlace', { n: g.rank }))}
                                  </span>
                                  <span className="rk-game__pts">
                                    {Math.round(g.points)}
                                    {/* What made this place worth more for THIS team: its joker,
                                        and the ladies point. Apart, because added in they are
                                        invisible and the ladder no longer explains the number. */}
                                    {g.joker > 0 && (
                                      <b className="rk-game__plus">{`+${Math.round(g.joker)} ${t('joker')}`}</b>
                                    )}
                                    {g.ladies > 0 && (
                                      <b className="rk-game__plus">{`+${Math.round(g.ladies)} ${t('rankingLadiesPoint')}`}</b>
                                    )}
                                  </span>
                                  <Icon name="angle-right" className="rk-game__go" />
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}

                        <dl className="rk-sum">
                          <div className="rk-sum__line">
                            <dt>{t('rankingBattlePoints')}</dt>
                            <dd>{Math.round(tm.battle_points)}</dd>
                          </div>
                          {bonusLines(tm).map((line) => (
                            <div className="rk-sum__line" key={line.key}>
                              <dt>{t(line.label)}</dt>
                              <dd>{`+${line.value}`}</dd>
                            </div>
                          ))}
                          <div className="rk-sum__line rk-sum__line--total">
                            <dt>{t('rankingTotal')}</dt>
                            <dd>{Math.round(tm.points)}</dd>
                          </div>
                          {/* What the wall shows right now, from the board's own query. Only when it
                              differs — otherwise it is the same number twice. */}
                          {tm.shown_points != null
                            && Math.abs(Number(tm.shown_points) - Number(tm.points)) > 0.01 && (
                            <div className="rk-sum__line rk-sum__line--shown">
                              <dt>{t('rankingOnTheWall')}</dt>
                              <dd>{Math.round(tm.shown_points)}</dd>
                            </div>
                          )}
                        </dl>

                        {held > 0 && !capLifted && (
                          <p className="ct-field__hint">
                            {t('rankingHeldBackCount', { count: held })}
                          </p>
                        )}
                        {mismatch && (
                          <p className="ce-note ce-note--warn">
                            {t('rankingBreakdownMismatch', { sum: Math.round(sum) })}
                          </p>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
            <p className="ct-field__hint">
              {anyBonus ? t('rankingLegend') : t('rankingLegendNoBonus')}
            </p>
            <p className="ct-field__hint">{t('rankingOpenHint')}</p>
          </>
        )}

        <div className="ce-actions">
          <button type="button" className="bravos-submit bravos-submit--ghost"
                  onClick={() => navigate(`/company-event/${eventId}/scoring`)}>
            <Icon name="star" /> {t('tournamentScoring')}
          </button>
          {/* The board for the wall. It shows the RELEASED standing, not this one — see the
              screen's own note. */}
          <button type="button" className="bravos-submit bravos-submit--ghost"
                  onClick={() => navigate(`/company-event/${eventId}/ranking/screen`)}>
            <Icon name="expand" /> {t('project')}
          </button>
        </div>
      </Card>
    </Base>
  );
};

export default CompanyEventRanking;
